import { Component, TemplateRef, ViewChild, OnInit, OnDestroy, inject } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { TableDetails } from '../../shared/tablefilters/table-details.model';
import { Column } from '../../shared/tablefilters/column.model';
import { AllyCommonService } from '../../../../../services/ally-common-service';
import { Exception, LaunchDarklyService, ToasterService } from '@anddone/ui-utilities';
import { ReportsService } from '../../../../../services/reports.service';
import { TableFilterService } from '../../shared/tablefilters/table-filter.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageConstants } from '../../../../../constants/message.constant';
import { TableComponent } from '../../shared/tablefilters/table/table.component';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { DatePipe } from '@angular/common';
import { ValidationConstant } from '../../../../../constants/validation.constant';
import { AppSetting } from '../../../../../constants/appsetting.constant';

@Component({
  selector: 'app-generated-reports',
  imports: [TranslateModule, NgbDropdownModule, FormsModule, TableComponent],
  templateUrl: './generated-reports.html',
  styleUrl: './generated-reports.scss',
  providers: [DatePipe],
})
export class GeneratedReports implements OnInit, OnDestroy {
  reportId: any;
  searchParamsData: any = {};
  pager: any = {};
  searchInputSub!: Subscription;
  generatedReportList: any[] = [];
  reportName: any;
  manualReportDate: any;
  @ViewChild('reportCell', { static: true })
  reportCell!: TemplateRef<any>;
  today: string = new Date().toISOString().split('T')[0];
  private destroy$ = new Subject<void>();
  tableActionSub!: Subscription;

  tableDetails: TableDetails = {
    tableId: 'generated-reports-table',
    searchPlaceholder: '',
    allowRowClick: false,
    rowClickURI: '',
    tableTitle: 'Generated Reports',
    defaultItemPerPage: ValidationConstant.defaultPageSize,
    sendDefault90DayFilter: false,
  };
  columns: Column[] = [
    {
      name: 'Report',
      sortState: 'none',
      isSortable: false,
      display: 'template',
      responseKey: 'reportFileName',
      requestKey: 'reportFileName',
      cellTemplate: this.reportCell,
      filter: {
        type: 'string',
        validation: ['required'],
      },
    },
    {
      name: 'Frequency',
      sortState: 'none',
      isSortable: true,
      display: 'normal',
      responseKey: 'frequency',
      requestKey: 'frequency',
      filter: {
        type: 'string',
        validation: ['required'],
      },
    },
    {
      name: 'Generated On',
      sortState: 'desc',
      isSortable: true,
      display: 'date',
      responseKey: 'createdOn',
      requestKey: 'createdOn',
      filter: {
        type: 'date',
        validation: ['required'],
      },
    },
    {
      name: 'Report Date',
      sortState: 'none',
      isSortable: true,
      display: 'dateOnly',
      responseKey: 'reportDate',
      requestKey: 'reportDate',
      filter: {
        type: 'date',
        validation: ['required'],
      },
    },
    {
      name: 'Transactions',
      sortState: 'none',
      isSortable: true,
      display: 'normal',
      responseKey: 'noOfTransactions',
      requestKey: 'noOfTransactions',
      filter: {
        type: 'string',
        validation: ['required'],
      },
    },
    {
      name: 'Amount',
      sortState: 'none',
      isSortable: true,
      display: 'amount',
      responseKey: 'totalNetSettlementAmount',
      requestKey: 'totalNetSettlementAmount',
      filter: {
        type: 'amount',
        validation: ['required'],
      },
    },
  ];
  featureFlags: any;

  public allyCommonService = inject(AllyCommonService);
  private toasterService = inject(ToasterService);
  private reportsService = inject(ReportsService);
  private tableFilterService = inject(TableFilterService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private launchDarklyService = inject(LaunchDarklyService);
  ngOnInit(): void {
    this.allyCommonService.setIsLoader(true);
    // Get all feature flags for the project and subscribe to changes
    this.featureFlags = this.launchDarklyService.getAllFeatureFlags(
      AppSetting.launchDarklyProjectName
    );
    this.launchDarklyService.LD_emitters[AppSetting.launchDarklyProjectName]?.subscribe(
      (allFlags: any) => {
        this.featureFlags = allFlags;
      }
    );
    this.reportId = this.activatedRoute.snapshot.params['tableRoute'];
    this.reportName = window?.history?.state?.reportName;
    this.tableDetails.tableId = `generated-reports-table-${this.reportId}`;
    /*** Table Filter subscriptions ***/
    this.tableFilterService.init(this.columns, this.tableDetails);

    this.searchInputSub = this.tableFilterService.searchInput
      .pipe(takeUntil(this.destroy$))
      .subscribe((searchInput) => {
        this.searchParamsData = searchInput.searchParams;
        this.pager = searchInput.pager;
        setTimeout(() => {
          this.findGeneratedReports(this.pager.currentPage);
        }, 500);
      });

    this.tableFilterService.registerSubscriptions(this.searchInputSub, this.tableActionSub);

    const col = this.columns.find((c) => c.responseKey === 'reportFileName');
    if (col) col.cellTemplate = this.reportCell;
  }

  generateSelectedDateReport() {
    const reqObj: any = {
      id: this.reportId,
      data: {},
    };
    if (this.manualReportDate && this.manualReportDate !== 'undefined') {
      reqObj.data.Date = this.allyCommonService.getFormattedDate(this.manualReportDate);
    }
    this.allyCommonService.setIsLoader(true);
    if (this.featureFlags?.enable_report_api_v3) {
      this.reportsService.generateReport(reqObj).subscribe({
        next: (res: any) => {
          this.allyCommonService.setIsLoader(false);
          if (res) {
            this.searchParamsData.SortField = 'createdOn';
            this.searchParamsData.Ascending = false;
            this.findGeneratedReports(1);
          } else {
            this.toasterService.showInfo(MessageConstants.report.reportDataNotAvailable, 'full');
          }
          this.generatedReportList = [];
        },
        error: (err) => {
          this.allyCommonService.setIsLoader(false);
          const toastMessage = Exception.exceptionMessage(err);
          if (err?.error?.message == 'Key_InvalidDate') {
            this.toasterService.showInfo(toastMessage.join(', '), 'full');
          } else {
            this.toasterService.showError(toastMessage.join(', '), 'full');
          }
        },
      });
    } else {
      this.reportsService.generateOldReport(reqObj)?.subscribe({
        next: (res: any) => {
          this.allyCommonService.setIsLoader(false);
          if (res) {
            this.searchParamsData.SortField = 'createdOn';
            this.searchParamsData.Asc = false;
            this.findGeneratedReports(1);
          } else {
            this.toasterService.showInfo(MessageConstants.report.reportDataNotAvailable, 'full');
          }
          this.generatedReportList = [];
        },
        error: (err) => {
          this.allyCommonService.setIsLoader(false);
          const toastMessage = Exception.exceptionMessage(err);
          if (err?.error?.message == 'Key_InvalidDate') {
            this.toasterService.showInfo(toastMessage.join(', '), 'full');
          } else {
            this.toasterService.showError(toastMessage.join(', '), 'full');
          }
        },
      });
    }
    this.manualReportDate = 'undefined';
  }

  findGeneratedReports(pageNumber: number) {
    if (pageNumber <= 0) {
      return;
    }
    if (this.pager.totalPages > 0) {
      if (pageNumber > this.pager.totalPages) {
        return;
      }
    }
    if (!this.searchParamsData.SortField) {
      this.searchParamsData.SortField = 'createdOn';
    }
    this.searchParamsData = this.tableFilterService.prepareSearchParamsForAPICall(pageNumber) || {};
    if (this.featureFlags?.enable_report_api_v3) {
      this.searchParamsData.PageIndex = pageNumber;
      if (Object.prototype.hasOwnProperty.call(this.searchParamsData, 'StartRow')) {
        delete this.searchParamsData.StartRow;
      }
      if (Object.prototype.hasOwnProperty.call(this.searchParamsData, 'Asc')) {
        this.searchParamsData.Ascending = this.searchParamsData.Asc;
        delete this.searchParamsData.Asc;
      }
    } else {
      if (Object.prototype.hasOwnProperty.call(this.searchParamsData, 'PageIndex')) {
        delete this.searchParamsData.PageIndex;
      }
      if (Object.prototype.hasOwnProperty.call(this.searchParamsData, 'Ascending')) {
        this.searchParamsData.Asc = this.searchParamsData.Ascending;
        delete this.searchParamsData.Ascending;
      }
    }

    const reqObj = {
      id: this.reportId,
      data: this.searchParamsData,
    };
    this.allyCommonService.setIsLoader(true);
    if (this.featureFlags?.enable_report_api_v3) {
      this.reportsService.getSpecificReports(reqObj)?.subscribe({
        next: (res: any) => {
          const records = res.value?.records || [];
          if (records.length === 0) {
            setTimeout(() => {
              this.allyCommonService.setIsLoader(false);
              this.tableDetails.tableData = [];
              this.tableFilterService.setTableDetails(this.tableDetails);
            }, 1000);
          } else {
            this.allyCommonService.setIsLoader(false);
            const mappedRes = {
              data: records,
              totalRowCount: res.value?.recordCount || 0,
            };
            this.tableFilterService.setPager(mappedRes, pageNumber, this.pager);
            this.generatedReportList = records;
            this.generatedReportList.forEach((val: { totalNetSettlementAmount?: number }) => {
              if (val.totalNetSettlementAmount) {
                val.totalNetSettlementAmount = Math.round(val.totalNetSettlementAmount * 100) / 100;
              }
            });
            this.tableDetails.tableData = this.generatedReportList || [];
            this.tableFilterService.setTableDetails(this.tableDetails);
          }
        },
        error: (e) => {
          this.allyCommonService.setIsLoader(false);
          const toastMessage = Exception.exceptionMessage(e);
          this.toasterService.showError(toastMessage.join(', '), 'full');
        },
      });
    } else {
      this.reportsService.getSpecificOldReports(reqObj)?.subscribe({
        next: (res: any) => {
          if (res.data.length == 0) {
            setTimeout(() => {
              this.allyCommonService.setIsLoader(false);
              this.tableDetails.tableData = [];
              this.tableFilterService.setTableDetails(this.tableDetails);
            }, 1000);
          } else {
            this.allyCommonService.setIsLoader(false);
            this.tableFilterService.setPager(res, pageNumber, this.pager);
            this.generatedReportList = res.data;
            this.generatedReportList.forEach((val: { totalNetSettlementAmount?: number }) => {
              if (val.totalNetSettlementAmount) {
                val.totalNetSettlementAmount = Math.round(val.totalNetSettlementAmount * 100) / 100;
              }
            });
            this.tableDetails.tableData = this.generatedReportList || [];
            this.tableFilterService.setTableDetails(this.tableDetails);
          }
        },
        error: (e) => {
          this.allyCommonService.setIsLoader(false);
          const toastMessage = Exception.exceptionMessage(e);
          this.toasterService.showError(toastMessage.join(', '), 'full');
        },
      });
    }
  }

  downloadReport(data: any) {
    const reqObj = {
      reportId: data?.reportId,
      logId: data?.id,
    };
    this.allyCommonService.setIsLoader(true);
    if (this.featureFlags?.enable_report_api_v3) {
      this.reportsService.downloadReport(reqObj).subscribe({
        next: (res: any) => {
          this.allyCommonService.setIsLoader(false);
          const downloadLink = res.value?.downloadLink;
          if (data.reportFileName?.includes('pdf')) {
            window.open(downloadLink, '_blank');
          } else {
            window.open(downloadLink, '_self');
          }
        },
        error: (e) => {
          this.allyCommonService.setIsLoader(false);
          const toastMessage = Exception.exceptionMessage(e);
          this.toasterService.showError(toastMessage.join(', '), 'full');
        },
      });
    } else {
      this.reportsService.downloadOldReport(reqObj).subscribe({
        next: (res: any) => {
          this.allyCommonService.setIsLoader(false);
          if (data.reportFileName?.includes('pdf')) {
            window.open(res.downloadLink, '_blank');
          } else {
            window.open(res.downloadLink, '_self');
          }
        },
        error: (e) => {
          this.allyCommonService.setIsLoader(false);
          const toastMessage = Exception.exceptionMessage(e);
          this.toasterService.showError(toastMessage.join(', '), 'full');
        },
      });
    }
  }

  backToReport() {
    this.router.navigate(['/ally/reports']);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
